import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Center, VStack, Heading, Text, Button, Alert, AlertIcon } from '@chakra-ui/react';
import { useAuth } from '@context/AuthContext';
import { useIsAdmin } from '@shared/hooks/useIsAdmin';

interface AdminRouteProps {
    children: React.ReactNode;
}

/**
 * Component that guards admin-only routes.
 *
 * Shows a 403 error page if the user is not an admin,
 * and redirects to /login if the user is not authenticated.
 */
const AdminRoute = ({ children }: AdminRouteProps): JSX.Element => {
    const { user, loading } = useAuth();
    const isAdmin = useIsAdmin();

    // Show a spinner while permissions are being checked
    if (loading) {
        return (
            <Center flex="1" bg="gray.50">
                <Text>Vérification des permissions...</Text>
            </Center>
        );
    }

    // If not authenticated, redirect to login
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // If authenticated but not an admin, show the 403 error page
    if (!isAdmin) {
        return (
            <Center flex="1" bg="gray.50" p={6}>
                <VStack spacing={6} maxW="md">
                    <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        Accès refusé
                    </Alert>

                    <VStack spacing={4} p={8} bg="white" borderRadius="lg" boxShadow="md" w="full">
                        <Heading as="h1" size="xl" color="red.500">
                            403
                        </Heading>
                        <Heading as="h2" size="md" textAlign="center">
                            Accès réservé aux administrateurs
                        </Heading>
                        <Text color="gray.600" textAlign="center">
                            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
                        </Text>
                        <Button as={Link} to="/app" colorScheme="blue" mt={4}>
                            Retour au Dashboard
                        </Button>
                    </VStack>
                </VStack>
            </Center>
        );
    }

    // If admin, render the content
    return <>{children}</>;
};

export default AdminRoute;
